import { useMemo, useState } from 'react';
import { ImageURISource } from 'react-native';
import { ImagePickerAsset } from 'expo-image-picker';
import { z } from 'zod';

import { BIO_MAX_LENGTH, USERNAME_REGEX, formatUsername } from '@pletly/shared';
import { prepareMediaFile, processSelectedMedia } from '@pletly/common';

import { useForm } from './useForm';

export const useEditInfoFields = (
	{
		email = undefined as string | undefined,
		username = '',
		info = '',
		firstName = '',
		lastName = '',
		restrictedAccess = false,
		receiveSummaryNotificationEmails = undefined as boolean | undefined,
		tags = undefined as string[] | undefined,
	},
	{ isCareReceiverProfile, canFullyEditCareRecipient, canEditTags } = {
		isCareReceiverProfile: false,
		canFullyEditCareRecipient: false,
		canEditTags: false,
	},
) => {
	const formSchema = useMemo(
		() =>
			z.object({
				username: z.string().nonempty().regex(USERNAME_REGEX).transform(formatUsername),
				firstName: isCareReceiverProfile ? z.string().optional() : z.string().nonempty(),
				lastName: isCareReceiverProfile ? z.string().optional() : z.string().nonempty(),
				email: z.undefined().or(z.string().email()),
				info: z.string().max(BIO_MAX_LENGTH).optional(),
				restrictedAccess: canFullyEditCareRecipient ? z.boolean().optional() : z.undefined(),
				receiveSummaryNotificationEmails: isCareReceiverProfile
					? z.undefined()
					: z.boolean().optional(),
				tags: !canEditTags ? z.undefined() : z.array(z.string()).optional(),
			}),
		[isCareReceiverProfile, canFullyEditCareRecipient, canEditTags],
	);
	const formMethods = useForm(formSchema, {
		defaultValues: {
			...(email ? { email } : {}),
			username,
			info,
			firstName,
			lastName,
			restrictedAccess: canFullyEditCareRecipient ? restrictedAccess : undefined,
			receiveSummaryNotificationEmails,
			tags: canEditTags ? tags : undefined,
		},
		resetOptions: { keepIsSubmitSuccessful: false },
	});
	const isChanged = formMethods.formState.isDirty;

	return {
		formMethods,
		isChanged,
	};
};

export const useEditProfilePhotoField = (currentSource?: ImageURISource) => {
	const [asset, setAsset] = useState<ImagePickerAsset>();

	const source = useMemo(
		() => (asset ? { uri: asset.uri } : currentSource),
		[asset, currentSource],
	);
	const isChanged = Boolean(asset);

	return {
		source,
		isChanged,
		asset,
		setAsset,
	};
};

export async function preparePhotoAsset(photoAsset?: ImagePickerAsset) {
	if (!photoAsset) {
		return;
	}
	const media = await processSelectedMedia(photoAsset);

	return prepareMediaFile(media);
}
